import { useEffect, useState } from "react";
import { ArrowRight, Mail, MapPin, Phone } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { apiFetch, sendQuoteEmail, IS_STATIC } from "@/lib/api";
import { getContent, loadRemoteContent, hasLocalOverrides } from "@/lib/staticData";
import Socials from "./Socials";

const SERVICES = [
  "Interior Painting",
  "Exterior Cladding",
  "Commercial Project",
  "Cabinet Refinishing",
  "Woodwork & Joinery",
  "Paving Paint",
  "Something Else",
];

const EMPTY = { name: "", email: "", phone: "", suburb: "", service: SERVICES[0], message: "" };

const inputCls =
  "w-full border border-neutral-300 bg-white px-4 py-3 text-sm text-[#171615] outline-none transition-colors placeholder:text-neutral-400 focus:border-[#A90401]";

const tel = (p) => `tel:+61${String(p).replace(/[^0-9]/g, "").replace(/^0/, "")}`;

export default function ContactQuoteSection() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [site, setSite] = useState(IS_STATIC ? getContent().settings : {});

  useEffect(() => {
    if (IS_STATIC) {
      loadRemoteContent().then((c) => {
        if (c && !hasLocalOverrides()) setSite(c.settings);
      });
      return;
    }
    apiFetch("/settings").then(setSite).catch(() => {});
  }, []);

  const phone1 = site.phone1 || "0406 646 061";
  const phone2 = site.phone2 || "0481 783 135";
  const email = site.notification_email || "Strovyn@gmail.com";
  const location = site.location || "Adelaide, South Australia";

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      if (!IS_STATIC) {
        await apiFetch("/quotes", { method: "POST", body: form });
      }
      sendQuoteEmail(form);
      toast.success("Request received. We'll reply within one business day.");
      setForm(EMPTY);
    } catch (err) {
      toast.error(err.message || "Could not send your request. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="bg-accent py-24 text-white sm:py-32" data-testid="contact-section">
      <div className="mx-auto grid max-w-7xl gap-14 px-4 sm:px-8 lg:grid-cols-2">
        <Reveal>
          <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-white/70">
            Get a quote
          </p>
          <h2 className="mt-4 font-serif text-6xl uppercase leading-[0.95] tracking-tight sm:text-7xl">
            Start<br />your
            <span className="text-outline-white block">project.</span>
          </h2>

          <div className="mt-12 space-y-5">
            {[phone1, phone2].filter(Boolean).map((p, i) => (
              <a key={p} href={tel(p)} data-testid={i === 0 ? "contact-phone-link" : "contact-phone-link-2"}
                className="group flex items-center gap-4 text-base transition-colors hover:text-white/80">
                <span className="flex h-10 w-10 items-center justify-center border border-white/40 transition-colors group-hover:bg-white group-hover:text-accent">
                  <Phone className="h-4 w-4" />
                </span>
                {p}
              </a>
            ))}
            <a href={`mailto:${email}`} data-testid="contact-email-link"
              className="group flex items-center gap-4 text-base transition-colors hover:text-white/80">
              <span className="flex h-10 w-10 items-center justify-center border border-white/40 transition-colors group-hover:bg-white group-hover:text-accent">
                <Mail className="h-4 w-4" />
              </span>
              {email}
            </a>
            <p className="flex items-center gap-4 text-base text-white/90">
              <span className="flex h-10 w-10 items-center justify-center border border-white/40">
                <MapPin className="h-4 w-4" />
              </span>
              {location}
            </p>
            <div className="pt-3">
              <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-white/60">
                Follow us
              </p>
              <Socials site={site} variant="red" />
            </div>
          </div>

          <p className="mt-12 font-mono text-[10px] uppercase leading-relaxed tracking-[0.25em] text-white/60">
            Serving greater Adelaide — Glenelg to the Hills,
            <br />Gawler to McLaren Vale. Mon–Sat, 7:00–17:00.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <form onSubmit={submit} className="space-y-4 bg-white p-6 sm:p-10" data-testid="quote-calculator-form">
            <div className="grid gap-4 sm:grid-cols-2">
              <input required placeholder="Full name" value={form.name} onChange={set("name")}
                data-testid="quote-name-input" className={inputCls} />
              <input required type="email" placeholder="Email" value={form.email} onChange={set("email")}
                data-testid="quote-email-input" className={inputCls} />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <input placeholder="Phone (optional)" value={form.phone} onChange={set("phone")}
                data-testid="quote-phone-input" className={inputCls} />
              <input placeholder="Adelaide suburb" value={form.suburb} onChange={set("suburb")}
                data-testid="quote-suburb-input" className={inputCls} />
            </div>
            <select value={form.service} onChange={set("service")}
              data-testid="quote-service-select" className={inputCls}>
              {SERVICES.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
            <textarea placeholder="Tell us about the space — rooms, surfaces, timeline..." rows={4}
              value={form.message} onChange={set("message")}
              data-testid="quote-message-input" className={`${inputCls} resize-none`} />
            <button type="submit" disabled={sending}
              data-testid="quote-submit-button"
              className="group flex w-full items-center justify-center gap-3 bg-accent px-6 py-4 font-mono text-xs uppercase tracking-[0.25em] text-white transition-colors hover:bg-[#171615] disabled:opacity-50">
              {sending ? "Sending..." : "Request My Quote"}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <p className="text-center font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">
              Free on-site consult across metro Adelaide
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
